import { ILoggedCustomer, ITabletData } from "api/types";
import { ApolloClient } from "apollo-boost";
import { AppStateContext } from "AppState";
import Button from "components/common/Button/Button";
import NumPad, { MediumCenteredNumPad } from "components/common/NumPad";
import PanelLayout from "components/common/PanelLayout/PanelLayout";
import PinInput from "components/common/PinInput";
import LeftPanel from "components/common/RightLeftLayout/Panels/LeftPanel";
import RightPanel from "components/common/RightLeftLayout/Panels/RightPanel";
import Text from "components/common/Text/Text";
import Widget from "components/common/Widget";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import { TabletDataContext } from "components/Gates/TabletDataGate";
import { shouldHideUsageType } from "components/UsageTypeSelector/utils";
import { History } from "history";
import * as React from "react";
import { ApolloConsumer } from "react-apollo";
import { Link } from "react-router-dom";
import { updatePin } from "utils/apis";
import delay from "utils/delay";
import {
  Container,
  FormGroup,
  RowContainer,
  StyledNumpad,
  StyledText
} from "./common/TabbedContainers";
import EditProfileTabs, { EditProfileTabNames } from "./tabs/EditProfileTabs";

interface IProps {
  history: History;
  location: any;
  hasNoInterests?: boolean;
}

interface IState {
  currentControl: string;
  newPin: string;
  confirmNewPin: string;
  goodConfirmNewPin?: boolean;
  wrongConfirmNewPin?: boolean;
  changeDone?: boolean;
  changeFailed?: boolean;
}

class ChangePin extends React.Component<
  IProps & {
    client: ApolloClient<any>;
    loggedCustomer: ILoggedCustomer | undefined;
    locationName: string;
    tabletData: ITabletData;
  },
  IState
> {
  constructor(
    props: IProps & {
      client: ApolloClient<any>;
      loggedCustomer: ILoggedCustomer | undefined;
      locationName: string;
      tabletData: ITabletData;
    }
  ) {
    super(props);
    this.state = {
      currentControl: "newPin",
      newPin: "",
      confirmNewPin: ""
    };
  }

  public render() {
    const { hasNoInterests, history, locationName, tabletData } = this.props;
    const {
      newPin,
      confirmNewPin,
      goodConfirmNewPin,
      wrongConfirmNewPin,
      changeDone
    } = this.state;
    return (
      <PanelLayout
        title={locationName}
        titleSecondary="Set New PIN"
        closeButtonHandler
      >
        <Container>
          <EditProfileTabs
            hideUsageTypeTab={shouldHideUsageType(tabletData)}
            hasNoInterests={hasNoInterests}
            walletStatus={this.props.location.state.walletStatus}
            history={history}
            currentTab={EditProfileTabNames.PinUpdate}
          />
          <RowContainer>
            <LeftPanel className="left">
              {changeDone ? (
                <>
                  <StyledText>
                    Your PIN has been successfully changed!
                  </StyledText>
                  <Link to="/">
                    <Button primary>Go back to Dashboard</Button>
                  </Link>
                </>
              ) : (
                <>
                  <FormGroup>
                    <Text
                      h4
                      alternate
                      onClick={this.onFocus("newPin")}
                      style={{ marginTop: 0 }}
                    >
                      Enter New PIN
                    </Text>
                    <PinInput
                      pin={"﹡﹡﹡﹡".slice(0, newPin.length)}
                      onClick={this.onFocus("newPin")}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Text h4 alternate onClick={this.onFocus("confirmNewPin")}>
                      Confirm New PIN
                    </Text>

                    <PinInput
                      pin={"﹡﹡﹡﹡".slice(0, confirmNewPin.length)}
                      goodPin={goodConfirmNewPin}
                      wrongPin={wrongConfirmNewPin}
                      onClick={this.onFocus("confirmNewPin")}
                    />
                  </FormGroup>
                </>
              )}
            </LeftPanel>
            <RightPanel className="right">
              <MediumCenteredNumPad>
                <NumPad onTap={this.onTap} />
              </MediumCenteredNumPad>
            </RightPanel>
          </RowContainer>
        </Container>
      </PanelLayout>
    );
  }

  private onFocus = (control: string) => () => {
    this.setState({ currentControl: control });
  };

  private onTap = (value: string) => {
    this.setState(state => {
      const currentValue = state[state.currentControl];
      let newValue = currentValue;
      if (value === "back") {
        newValue = currentValue.slice(0, currentValue.length - 1);
      } else if (currentValue.length < 4) {
        newValue = `${currentValue}${value}`;
      }
      if (state.currentControl === "newPin") {
        return {
          newPin: newValue,
          confirmNewPin: state.confirmNewPin,
          currentControl: newValue.length >= 4 ? "confirmNewPin" : "newPin",
          goodConfirmNewPin:
            state.confirmNewPin.length >= 4 &&
            newValue.length >= 4 &&
            state.confirmNewPin === newValue,
          wrongConfirmNewPin:
            state.confirmNewPin.length >= 4 &&
            newValue.length >= 4 &&
            state.confirmNewPin !== newValue,
          changeFailed: false
        };
      } else if (state.currentControl === "confirmNewPin") {
        return {
          newPin: state.newPin,
          confirmNewPin: newValue,
          currentControl: "confirmNewPin",
          goodConfirmNewPin:
            state.newPin.length >= 4 &&
            newValue.length >= 4 &&
            state.newPin === newValue,
          wrongConfirmNewPin:
            state.newPin.length >= 4 &&
            newValue.length >= 4 &&
            state.newPin !== newValue,
          changeFailed: false
        };
      }
      return {
        newPin: state.newPin,
        confirmNewPin: state.confirmNewPin,
        currentControl: state.currentControl,
        changeFailed: false
      };
    }, this.handlePin);
  };

  private handlePin = async () => {
    if (
      this.state.newPin === this.state.confirmNewPin &&
      this.state.confirmNewPin.length >= 4
    ) {
      await delay(1000);
      try {
        const changed = await updatePin(
          this.props.client,
          this.props.loggedCustomer ? this.props.loggedCustomer.authToken : "",
          this.state.newPin
        );
        this.setState({
          changeDone: changed,
          changeFailed: !changed
        });
      } catch (e) {
        console.error("Failed to change pin due to error", e);
        this.setState({
          changeDone: false,
          changeFailed: true
        });
      }
    }
  };
}

export default (props: IProps) => (
  <ApolloConsumer>
    {client => (
      <CustomerDataContext.Consumer>
        {loggedCustomer => (
          <TabletDataContext.Consumer>
            {tabletData =>
              tabletData && (
                <AppStateContext.Consumer>
                  {([appState]) => (
                    <ChangePin
                      client={client}
                      loggedCustomer={loggedCustomer}
                      hasNoInterests={tabletData.hasNoInterests}
                      locationName={appState.locationName}
                      tabletData={tabletData}
                      {...props}
                    />
                  )}
                </AppStateContext.Consumer>
              )
            }
          </TabletDataContext.Consumer>
        )}
      </CustomerDataContext.Consumer>
    )}
  </ApolloConsumer>
);



// WEBPACK FOOTER //
// ./src/screens/Main/edit-profile/changePin.tsx